// src/components/ProductPhoto.js
//
// One photo, filling the box it is given.
//
// What the visitor sees, in order: the tiny blurred copy that travels with the
// photo's record, at once; then the photo itself fading in over it — but only
// once it is downloaded *and* decoded, so the fade never stalls half way while
// the browser unpacks a large image on the main thread. The browser picks the
// width from `srcset` and `sizes`, so a phone is never sent the desktop copy.
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { Wheat } from 'lucide-react';
import {
  onProductPhotosChange,
  photoUrl,
  rememberedProductPhotos,
  srcSetOf,
} from '../photos';

const ProductPhoto = ({
  photo,
  alt = '',
  sizes = '100vw',
  eager = false,
  priority,
  onReady,
  pending = false,
  sx,
}) => {
  const imgRef = useRef(null);
  // loading → shown (fading in) → settled (placeholder no longer needed).
  const [state, setState] = useState('loading');
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  const key = photo?.key;

  // A photo already in the browser's cache can be complete before the first
  // paint; it goes straight to settled rather than fading in from nothing.
  useLayoutEffect(() => {
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth > 0) {
      setState('settled');
      onReadyRef.current?.();
    } else {
      setState('loading');
    }
  }, [key]);

  const handleLoad = () => {
    const img = imgRef.current;
    const show = () => {
      setState((current) => (current === 'loading' ? 'shown' : current));
      onReadyRef.current?.();
    };
    // decode() resolves once the pixels are ready to paint, so the fade that
    // follows is pure compositing. Browsers without it just show the photo.
    if (img && typeof img.decode === 'function') img.decode().then(show, show);
    else show();
  };

  const showPlaceholder = photo?.placeholder && state !== 'settled' && state !== 'failed';

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: 'action.hover',
        ...sx,
      }}
    >
      {showPlaceholder && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url("${photo.placeholder}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            // The blur hides the 24 pixel copy's blockiness; the scale hides
            // the soft edge the blur leaves behind.
            filter: 'blur(14px)',
            transform: 'scale(1.2)',
          }}
        />
      )}

      {photo && state !== 'failed' ? (
        <Box
          component="img"
          key={key}
          ref={imgRef}
          src={photoUrl(photo)}
          srcSet={srcSetOf(photo)}
          sizes={sizes}
          width={photo.width}
          height={photo.height}
          alt={alt}
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          fetchPriority={priority}
          onLoad={handleLoad}
          onError={() => setState('failed')}
          onTransitionEnd={() => setState('settled')}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            opacity: state === 'loading' ? 0 : 1,
            transition: state === 'settled' ? 'none' : 'opacity .4s ease',
          }}
        />
      ) : pending ? null : (
        <Box
          aria-label={photo ? undefined : alt || undefined}
          role={photo ? undefined : 'img'}
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'text.disabled',
          }}
        >
          <Wheat size={28} strokeWidth={1.5} />
        </Box>
      )}
    </Box>
  );
};

/**
 * A product's cover photo as a thumbnail, for the cart and checkout.
 *
 * Those pages read the stock rows themselves, but only after they have drawn;
 * the cover comes from what the last stock read anywhere on the site said, so
 * it is usually right from the first paint. Until a read has said anything the
 * square stays plain, rather than showing a built-in photo that might then be
 * swapped for the one the admin uploaded.
 */
export const ProductThumb = ({ productId, alt = '', size, sx }) => {
  const [photos, setPhotos] = useState(() => rememberedProductPhotos(productId));
  useEffect(() => {
    setPhotos(rememberedProductPhotos(productId));
    return onProductPhotosChange(() => setPhotos(rememberedProductPhotos(productId)));
  }, [productId]);

  return (
    <ProductPhoto
      photo={photos?.[0] || null}
      pending={photos === null}
      alt={alt}
      sizes={`${size}px`}
      sx={sx}
    />
  );
};

export default ProductPhoto;
